<template>
  <div>
    <div class="item-info mb-2 d-flex">
      <b-media>
        <template v-slot:aside>
          <b-img v-if='activeLot.primary_image' :src="activeLot.primary_image" width="120" alt="placeholder"></b-img>
          <b-img v-else src="@/assets/no-image-available.png" width="120"/>
        </template>
        <div>
          <h3>{{ $t('Lot') }}: {{ activeLot.lot_no }}</h3>
        </div>
      </b-media>
    </div>

    <div class="card mb-2">
      <div class="card-header">
        <b>{{ $t("Lot Information") }}</b>
      </div>
      <div class="card-body overflow-auto" style="height: 10em">
        <dynamic-fields-info-table :dynamic-fields="activeLot.dynamic_fields" skip-empty-values v-if="sortedDynamicFieldsTemplate"
                                   :dynamic-fields-template="sortedDynamicFieldsTemplate"></dynamic-fields-info-table>
      </div>
    </div>

    <div class="d-flex">
      <b-card :class="{'mr-2': activeLot.buyer}" style="flex: 1" class="mb-2">
        <template v-slot:header>
          <b>{{ $t('Vendor') }} - {{ activeLot.vendor.display_name }}</b>
        </template>
        <b-card-text>
          <table class="info-table table table-sm mb-0">
            <tr>
              <td class="border-top-0">{{ $t('Total Lots') }}</td>
              <td class="border-top-0">{{ activeLot.vendor.total_lots }}</td>
            </tr>
            <tr>
              <td>{{ $t('Total Sold Lots') }}</td>
              <td>{{ activeLot.vendor.total_sold }}</td>
            </tr>
            <tr>
              <td>{{ $t('Total Sold Amount') }}</td>
              <td>{{ activeLot.vendor.total_sold_amount | asCurrency(saleInfo.currency.symbol) }}</td>
            </tr>
          </table>
        </b-card-text>
      </b-card>
      <b-card style="flex: 1" class="mb-2" v-if="activeLot.buyer">
        <template v-slot:header>
          <b>{{ $t('Buyer') }} - {{ activeLot.buyer.display_name }}</b>
        </template>
        <b-card-text>
          <table class="info-table table table-sm mb-0">
            <tr>
              <td class="border-top-0">{{ $t('Total Spent') }}</td>
              <td class="border-top-0">{{ activeLot.buyer.total_spent | asCurrency(saleInfo.currency.symbol) }}</td>
            </tr>
            <tr>
              <td>{{ $t('Bidding Limit') }}</td>
              <td>
                <template v-if="activeLot.buyer.credit_limit && activeLot.buyer.credit_limit > 0">
                  {{ activeLot.buyer.credit_limit | asCurrency(saleInfo.currency.symbol) }}
                </template>
                <template v-else>
                  &infin;
                </template>
              </td>
            </tr>
            <tr v-if="activeLot.buyer.credit_limit && activeLot.buyer.credit_limit > 0">
              <td>{{ $t('Available Credit') }}</td>
              <td>
                {{ activeLot.buyer.left_credit_limit | asCurrency(saleInfo.currency.symbol) }}
              </td>
            </tr>
          </table>
        </b-card-text>
      </b-card>
    </div>

    <div class="d-flex mb-2">
      <b-card style="flex: 1" class="mr-2">
        <template v-slot:header>
          <b>{{ $t('Overview') }}</b>
        </template>
        <b-card-text>
          <table class="info-table table table-sm mb-0">
            <tr>
              <td class="border-top-0">{{ $t('Stock') }}</td>
              <td class="border-top-0">
                {{ activeLot.stock_no }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('Telephone bids') }}</td>
              <td>
                {{ activeLot.total_telephone_bids }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('Reserve') }}</td>
              <td>
                {{ activeLot.reserve | asCurrency(saleInfo.currency.symbol) }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('Estimates') }}</td>
              <td>
              <span v-if="activeLot.low && activeLot.high">
                {{ activeLot.low | asCurrency(saleInfo.currency.symbol) }}
                - {{ activeLot.high | asCurrency(saleInfo.currency.symbol) }}
              </span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('Last Bid Amount') }}</td>
              <td>
              <span v-if="activeLot.last_bid_amount">
                {{ activeLot.last_bid_amount | asCurrency(saleInfo.currency.symbol) }}
              </span>
              </td>
            </tr>
          </table>
        </b-card-text>
      </b-card>
      <b-card style="flex: 1">
        <template v-slot:header>
          <b>{{ $t('Commissions') }}</b>
        </template>
        <b-card-text>
          <commissions-view-table :commissions="activeLot.commissions"/>
        </b-card-text>
      </b-card>
    </div>

    <b-card class="mb-2" v-if="activeLot.pre_bids.length > 0">
      <template v-slot:header>
        <b>{{ $t('Commission Bids') }}</b>
      </template>
      <b-card-text class="p-0">
        <table class="info-table table table-sm table-bordered mb-0">
          <tr>
            <th>{{ $t('Paddle') }}</th>
            <th>{{ $t('Customer') }}</th>
            <th>{{ $t('Bid') }}</th>
            <th style="width: 6em">{{ $t('Status') }}</th>
          </tr>
          <tr v-for="(bid, index) in activeLot.pre_bids" :key="index">
            <td style="width: 6em">{{ bid.paddle_number }}</td>
            <td>{{ bid.display_name }}</td>
            <td>{{ bid.amount | asCurrency(saleInfo.currency.symbol) }}</td>
            <td style="width: 10em">
              <b-form-select @change="onStatusChange(bid)" v-model="bid.status" :options="statuses"/>
            </td>
          </tr>
        </table>
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
  import {WS_CREATE_PRE_BID, WS_UPDATE_PRE_BID} from "../constants";
  import {createNamespacedHelpers} from "vuex";
  import i18n from "../../shared/i18n";
  import PreBidsService from "../pre-bids/PreBidsService";
  import CommissionsViewTable from "@/modules/receipt/view/dashboard/CommissionsViewTable";
  import DynamicFieldsInfoTable from "@/modules/inventory/view/dashboard/DynamicFieldsInfoTable";
  import helperService from "@/core/services/helper.service";

  const {mapActions} = createNamespacedHelpers('liveBids');

  export default {
    name: "ActiveLotDetails",
    components: {DynamicFieldsInfoTable, CommissionsViewTable},
    props: {
      activeLot: {
        type: Object,
        required: true
      },
      saleInfo: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        statuses: [
          {text: i18n.t('Rejected'), value: 'rejected'},
          {text: i18n.t('Pending'), value: 'pending'},
          {text: i18n.t('Confirmed'), value: 'confirmed'},
        ],
      }
    },
    computed: {
      sortedDynamicFieldsTemplate() {
        return helperService.calculateDynamicFieldsTemplateFromFields(this.activeLot.dynamic_fields, true)
      }
    },
    methods: {
      ...mapActions(['updateActiveLotPreBid', 'addActiveLotPreBid', 'updateLotPreBid', 'addLotPreBid']),
      async onStatusChange(bid) {
        let response = await PreBidsService.batchConfirmation([{uuid: bid.uuid, status: bid.status}]);
        if (response.success) {
          this.$toast('success', this.$i18n.t('Commission bid was successfully updated'));
          this.updateActiveLotPreBid(bid);
        }
      },
      onBidCreate(data) {
        this.$toast('success',
            this.$i18n.t("Commission bid under Lot {lot_no} has just been updated for {display_name}", {
              display_name: data.display_name,
              lot_no: data.lot_no
            })
        );

        if (data.sale_no !== this.saleInfo.sale_no) {
          return
        }

        if (data.lot_no === this.activeLot.lot_no) {
          this.addActiveLotPreBid(data);
        } else {
          this.addLotPreBid(data);
        }
      },
      onBidUpdate(data) {
        this.$toast('success',
            this.$i18n.t("Commission bid under Lot {lot_no} has just been created for {display_name}", {
              display_name: data.display_name,
              lot_no: data.lot_no
            })
        );

        if (data.sale_no !== this.saleInfo.sale_no) {
          return
        }

        if (data.lot_no === this.activeLot.lot_no) {
          this.updateActiveLotPreBid(data);
        } else {
          this.updateLotPreBid(data);
        }
      },
    },
    mounted() {
      this.$ws.listen(`live_bids.${WS_CREATE_PRE_BID}`, this.onBidCreate);
      this.$ws.listen(`live_bids.${WS_UPDATE_PRE_BID}`, this.onBidUpdate);
    },
    beforeDestroy() {
      this.$ws.remove(`live_bids.${WS_CREATE_PRE_BID}`);
      this.$ws.remove(`live_bids.${WS_UPDATE_PRE_BID}`);
    }
  }
</script>

<style lang="scss" scoped>
  .item-info {
    .media {
      flex: 1;
      height: 100%;

      .media-body {
        overflow: auto;
        height: 100%;
      }
    }
  }
</style>
